import { useCallback, useEffect, useState } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import Layout from './components/Layout';
import AddListing from './pages/AddListing';
import KeyGate from './pages/KeyGate';
import Search from './pages/Search';
import { Session, clear, load, save } from './session';
import { UsageResponse, validateKey } from './api';

/**
 * The public, always-on sandbox key — search-only, baked in at build time
 * (see Dockerfile). Distinct from a prospect's own emailed key: it belongs to
 * one dedicated `public-sandbox` tenant, never a per-prospect one. See
 * docs/PUBLIC_SANDBOX.md for how that tenant is created, seeded and quota-capped.
 */
export const PUBLIC_SANDBOX_KEY = import.meta.env.VITE_PUBLIC_SANDBOX_KEY || '';

/**
 * `/search`, carrying over a `q` the visitor arrived with. The marketing site's
 * hero sends its query as `/?sandbox=1&q=…` (website/src/components/
 * HeroSandbox.tsx), and the hops from `/` to `/search` — sign-in, or the
 * redirect for a visitor already signed in — must not drop it.
 *
 * The app routes on the hash (main.tsx), so the router's own query string is
 * the part after `#`; the hero's `q` is in the page's real one, before it.
 * Callers pass whichever they have, the router's first.
 */
function searchPath(search: string): string {
  const q = new URLSearchParams(search).get('q');
  return q ? `/search?${new URLSearchParams({ q })}` : '/search';
}

/**
 * The gate is the app's only piece of state that matters.
 *
 * Rule from the brief, enforced here rather than per page: with no valid key the
 * visitor sees the key form and nothing else — no search box, no listing form,
 * no route that renders either. Every authenticated route is inside the
 * `session` branch, so there is no path to them to forget to guard.
 */
export default function App() {
  const [session, setSession] = useState<Session | null>(() => load());
  const [revalidating, setRevalidating] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const signIn = useCallback((key: string, usage: UsageResponse) => {
    setSession(save(key, usage));
    navigate(searchPath(window.location.search));
  }, [navigate]);

  const signOut = useCallback(() => {
    clear();
    setSession(null);
    navigate('/');
  }, [navigate]);

  /**
   * A stored key is re-checked on load, once.
   *
   * Without this, a demo whose trial has since expired or been suspended renders
   * a full signed-in UI in which every action fails — the worst version of this
   * page to put in front of a prospect. Better to send them back to the form
   * with a reason.
   */
  useEffect(() => {
    if (!session || revalidating) return;
    let cancelled = false;
    setRevalidating(true);
    validateKey(session.key)
      .then((usage) => {
        if (!cancelled) setSession(save(session.key, usage));
      })
      .catch((error: unknown) => {
        // Only a definitive rejection signs them out. A network blip or an API
        // restart must not throw away a working key.
        const status = (error as { status?: number })?.status;
        if (!cancelled && (status === 401 || status === 402)) signOut();
      });
    return () => {
      cancelled = true;
    };
    // Runs once per mount: re-running on every session change would re-validate
    // after each search's document-count refresh.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * The "See it in action" link on the marketing site opens `/?sandbox=1`.
   * That flag, not the key itself, travels in the URL — the key stays out of
   * browser history and server logs, matching how VITE_SEARCH_API_URL is
   * already handled as build-time-only configuration.
   *
   * This still goes through the same validateKey → signIn path a pasted key
   * uses, so KeyGate's rule holds: nothing renders without a key having been
   * presented, it's just presented automatically here instead of typed.
   */
  useEffect(() => {
    if (session) return;
    if (!PUBLIC_SANDBOX_KEY) return;
    if (new URLSearchParams(window.location.search).get('sandbox') !== '1') return;

    validateKey(PUBLIC_SANDBOX_KEY)
      .then((usage) => signIn(PUBLIC_SANDBOX_KEY, usage))
      .catch((error: unknown) => {
        // Falls through to the normal KeyGate form — a misconfigured or
        // revoked sandbox key must not be a broken page for a visitor who was
        // never going to have a key of their own anyway.
        console.error('public sandbox sign-in failed', error);
      });
    // Runs once: signIn navigates to /search, and must not fire again and
    // override a key a visitor pastes by hand afterwards.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!session) {
    return (
      <Routes>
        <Route path="/" element={<KeyGate onSignIn={signIn} />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    );
  }

  return (
    <Layout session={session} onSignOut={signOut}>
      <Routes>
        <Route path="/search" element={<Search session={session} />} />
        <Route path="/add" element={<AddListing session={session} />} />
        <Route path="*" element={<Navigate to={searchPath(location.search || window.location.search)} replace />} />
      </Routes>
    </Layout>
  );
}
